import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { get, patch } from '../lib/api';
import { Avatar, ErrorNote, Spinner } from '../components/ui';
import NotificationCard from '../components/NotificationCard';
import { WEEKDAYS } from '../lib/format';
import { WORD_FIELDS, type Settings } from '../lib/types';

const EMOJIS = ['🧒', '🧑', '👦', '👧', '👩', '👨', '🦸', '🐯', '🦊', '🐼', '🐧', '🦄', '⚽️', '🎸', '🚀', '🌟'];

export default function Profile(): JSX.Element {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [pin, setPin] = useState('');
  const [pinSaved, setPinSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user) return <Spinner />;

  async function changePin() {
    setError(null);
    setPinSaved(false);
    if (!/^\d{4,8}$/.test(pin)) {
      setError('PIN must be 4-8 digits.');
      return;
    }
    try {
      await patch(`/api/users/${user!.id}`, { pin });
      setPin('');
      setPinSaved(true);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function pickEmoji(emoji: string) {
    await patch(`/api/users/${user!.id}`, { emoji });
    await refresh();
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 8 }}>
        <Avatar emoji={user.emoji} large />
        <div>
          <h1 style={{ marginBottom: 0 }}>{user.name}</h1>
          <span className="tiny muted">{user.role === 'parent' ? 'Parent' : 'Kid'}</span>
        </div>
      </div>

      <ErrorNote error={error} />
      <NotificationCard />

      <div className="card">
        <h3>Your avatar</h3>
        <div className="chips">
          {EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={`chip${user.emoji === emoji ? ' on' : ''}`}
              style={{ fontSize: 20 }}
              onClick={() => void pickEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <h3>Change PIN</h3>
        <label className="field">
          <span>New PIN (4-8 digits)</span>
          <input
            className="pin-input"
            inputMode="numeric"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
          />
        </label>
        <button className="btn block secondary" type="button" onClick={changePin}>
          {pinSaved ? 'PIN updated ✓' : 'Update PIN'}
        </button>
      </div>

      {user.role === 'parent' && <HouseholdSettings />}
      {user.role === 'parent' && <Diagnostics />}

      <button className="btn danger block" type="button" style={{ marginTop: 16 }} onClick={() => {
          navigate('/', { replace: true });
          void logout();
        }}>
        Sign out
      </button>
    </>
  );
}

/**
 * What is actually stored, in plain text. It exists for the moment when the
 * app shows something that does not match what you set up: read it here, or
 * copy it and send it to whoever is helping you.
 */
function Diagnostics(): JSX.Element {
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      const res = await get<{ text: string }>('/api/diagnostics');
      setText(res.text);
      setCopied(false);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setError('Could not copy — select the text and copy it by hand.');
    }
  }

  return (
    <div className="card">
      <h3>What the app has stored</h3>
      <p className="tiny muted" style={{ marginTop: 0 }}>
        Every chore with its real schedule, the shared jobs, and the last few ticks. Useful when
        something on a kid's screen doesn't match what you set up. No PINs or keys are included, so
        it is safe to send on.
      </p>
      <ErrorNote error={error} />
      <div className="row">
        <button className="btn grow" type="button" onClick={() => void run()} disabled={busy}>
          {busy ? 'Reading…' : text ? 'Read again' : 'Show me'}
        </button>
        {text && (
          <button className="btn ghost" type="button" onClick={() => void copy()}>
            {copied ? 'Copied ✓' : 'Copy'}
          </button>
        )}
      </div>
      {text && <pre className="diagnostics">{text}</pre>}
    </div>
  );
}

function HouseholdSettings(): JSX.Element {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    get<{ settings: Settings }>('/api/settings')
      .then((res) => setSettings(res.settings))
      .catch((err) => setError((err as Error).message));
  }, []);

  if (!settings) return <div className="card"><Spinner /></div>;

  function update<K extends keyof Settings>(key: K, value: Settings[K]) {
    setSettings((current) => (current ? { ...current, [key]: value } : current));
    setSaved(false);
  }

  /** Clearing every word puts the playful defaults back, server-side. */
  async function resetWords() {
    setError(null);
    try {
      const res = await patch<{ settings: Settings }>(
        '/api/settings',
        Object.fromEntries(WORD_FIELDS.map((field) => [field.key, ''])),
      );
      setSettings(res.settings);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function save() {
    setError(null);
    try {
      const res = await patch<{ settings: Settings }>('/api/settings', {
        householdName: settings!.householdName,
        timezone: settings!.timezone,
        currency: settings!.currency,
        quietHoursStart: settings!.quietHoursStart,
        quietHoursEnd: settings!.quietHoursEnd,
        parentReviewEnabled: settings!.parentReviewEnabled,
        parentReviewWeekday: settings!.parentReviewWeekday,
        parentReviewTime: settings!.parentReviewTime,
        parentMonthlyReviewEnabled: settings!.parentMonthlyReviewEnabled,
        schoolQuietEnabled: settings!.schoolQuietEnabled,
        schoolQuietStart: settings!.schoolQuietStart,
        schoolQuietEnd: settings!.schoolQuietEnd,
        saveNudgeEnabled: settings!.saveNudgeEnabled,
        saveNudgeTime: settings!.saveNudgeTime,
        houseStreakEnabled: settings!.houseStreakEnabled,
        houseStreakReward: settings!.houseStreakReward,
        ...Object.fromEntries(WORD_FIELDS.map((field) => [field.key, settings![field.key]])),
      });
      setSettings(res.settings);
      setSaved(true);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="card">
      <h3>Household</h3>
      <ErrorNote error={error} />
      <label className="field">
        <span>Name</span>
        <input value={settings.householdName} onChange={(e) => update('householdName', e.target.value)} />
      </label>
      <div className="row">
        <label className="field grow">
          <span>Currency</span>
          <input value={settings.currency} onChange={(e) => update('currency', e.target.value)} />
        </label>
        <label className="field grow">
          <span>Timezone</span>
          <input value={settings.timezone} onChange={(e) => update('timezone', e.target.value)} />
        </label>
      </div>
      <div className="row">
        <label className="field grow">
          <span>No reminders after</span>
          <input type="time" value={settings.quietHoursStart} onChange={(e) => update('quietHoursStart', e.target.value)} />
        </label>
        <label className="field grow">
          <span>…until</span>
          <input type="time" value={settings.quietHoursEnd} onChange={(e) => update('quietHoursEnd', e.target.value)} />
        </label>
      </div>

      <div className="divider" />
      <h3>The kids' reminders</h3>
      <div className="toggle">
        <span className="small">
          Quiet during school
          <br />
          <span className="tiny muted">No reminders on weekdays inside this window.</span>
        </span>
        <input
          type="checkbox"
          checked={settings.schoolQuietEnabled}
          onChange={(e) => update('schoolQuietEnabled', e.target.checked)}
        />
      </div>
      <div className="row">
        <label className="field grow">
          <span>From</span>
          <input type="time" value={settings.schoolQuietStart} onChange={(e) => update('schoolQuietStart', e.target.value)} />
        </label>
        <label className="field grow">
          <span>Until</span>
          <input type="time" value={settings.schoolQuietEnd} onChange={(e) => update('schoolQuietEnd', e.target.value)} />
        </label>
      </div>
      <div className="toggle">
        <span className="small">
          Last-chance nudge
          <br />
          <span className="tiny muted">One evening reminder, only when a streak is genuinely at stake.</span>
        </span>
        <input
          type="checkbox"
          checked={settings.saveNudgeEnabled}
          onChange={(e) => update('saveNudgeEnabled', e.target.checked)}
        />
      </div>
      <label className="field">
        <span>Sent at</span>
        <input type="time" value={settings.saveNudgeTime} onChange={(e) => update('saveNudgeTime', e.target.value)} />
      </label>

      <div className="divider" />
      <h3>House streak</h3>
      <div className="toggle">
        <span className="small">
          Count days when everyone finished
          <br />
          <span className="tiny muted">A shared run the kids build together, instead of competing.</span>
        </span>
        <input
          type="checkbox"
          checked={settings.houseStreakEnabled}
          onChange={(e) => update('houseStreakEnabled', e.target.checked)}
        />
      </div>
      <label className="field">
        <span>Reward at every 7 days</span>
        <input
          value={settings.houseStreakReward}
          onChange={(e) => update('houseStreakReward', e.target.value)}
          placeholder="pizza night"
        />
      </label>

      <div className="divider" />
      <h3>Words the kids see</h3>
      <p className="tiny muted" style={{ marginTop: 0 }}>
        Only on the kids' screen — this page and the chore editor keep the plain names.
      </p>
      {WORD_FIELDS.map((field) => (
        <label className="field" key={field.key}>
          <span>{field.label}</span>
          <input
            value={settings[field.key]}
            maxLength={40}
            onChange={(e) => update(field.key, e.target.value)}
          />
        </label>
      ))}
      <button className="btn ghost small" type="button" onClick={resetWords}>
        Put the default words back
      </button>

      <div className="divider" />
      <h3>Remind us to check</h3>
      <div className="toggle">
        <span className="small">Weekly summary</span>
        <input
          type="checkbox"
          checked={settings.parentReviewEnabled}
          onChange={(e) => update('parentReviewEnabled', e.target.checked)}
        />
      </div>
      <div className="row">
        <label className="field grow">
          <span>Day</span>
          <select
            value={settings.parentReviewWeekday}
            onChange={(e) => update('parentReviewWeekday', Number(e.target.value))}
          >
            {WEEKDAYS.map((day) => (
              <option key={day.value} value={day.value}>
                {day.long}
              </option>
            ))}
          </select>
        </label>
        <label className="field grow">
          <span>Time</span>
          <input type="time" value={settings.parentReviewTime} onChange={(e) => update('parentReviewTime', e.target.value)} />
        </label>
      </div>
      <div className="toggle">
        <span className="small">Monthly summary on the last day of the month</span>
        <input
          type="checkbox"
          checked={settings.parentMonthlyReviewEnabled}
          onChange={(e) => update('parentMonthlyReviewEnabled', e.target.checked)}
        />
      </div>

      <button className="btn block" type="button" style={{ marginTop: 12 }} onClick={save}>
        {saved ? 'Saved ✓' : 'Save household settings'}
      </button>
    </div>
  );
}
